import { observer } from 'mobx-react-lite'
import { Forbidden } from 'fejl'
import { ContentLoading } from '@/components/ContentLoading'
import { AccessDeniedView } from '@/components/AccessDeniedView'
import { ContentError } from '@/components/ContentError'
import { match } from '@/utils/pattern-match-util'
import { type CreateJobDialogViewModel } from './CreateJobDialogViewModel'
import {
  CreateJobDialog,
  CreateJobDialogBillingStepButtons,
  CreateJobDialogBulkQuestionsStepButtons,
  CreateJobDialogHeader,
  CreateJobDialogQuestionsStepButtons,
  CreateJobDialogUnansweredFeedback,
} from '@/features/jobs/drafting/components/CreateJobDialog'
import { JobCreationQuestionsContainer } from '@/features/jobs/drafting/components/CreateJobDialog/JobCreationQuestions/JobCreationQuestionsContainer'
import { JobCreationBillingContainer } from '@/features/jobs/drafting/components/CreateJobDialog/JobCreationBilling/JobCreationBillingContainer'
import { useFeatures } from '@/contexts/root-store-context'
import { useActivation } from '@/hooks/view-model-hooks'

export interface CreateJobDialogContainerProps {
  vm: CreateJobDialogViewModel
}

export const CreateJobDialogContainer = observer(
  function CreateJobDialogContainer({ vm }: CreateJobDialogContainerProps) {
    const { shellInfo } = useFeatures(({ shell }) => ({
      shellInfo: shell.shellInfo,
    }))
    useActivation(vm)
    return (
      <CreateJobDialog
        showing={vm.modal.showing}
        onClose={vm.modal.close}
        header={<CreateJobDialogHeaderContainer vm={vm} />}
        footerFeedback={match(vm.modalStep, {
          Questions: () => <QuestionsUnansweredFeedbackContainer vm={vm} />,
          Billing: () => null,
        })}
        footerActions={match(vm.modalStep, {
          Questions: () => <QuestionsStepButtonGroupContainer vm={vm} />,
          Billing: () => <BillingStepButtonGroupContainer vm={vm} />,
        })}
      >
        {vm.edit.match({
          pending: () => <ContentLoading />,
          rejected: (err) =>
            err instanceof Forbidden ? (
              <AccessDeniedView corporateMode={shellInfo.corporateMode} />
            ) : (
              <ContentError size="lg" error={err} />
            ),
          resolved: (editor) =>
            vm.canViewEditor ? (
              match(vm.modalStep, {
                Questions: () => (
                  <JobCreationQuestionsContainer
                    vm={editor}
                    jobWasCreated={vm.jobWasCreated}
                    isValidationShown={vm.isValidationShown}
                  />
                ),
                Billing: () => <JobCreationBillingContainer vm={editor} />,
              })
            ) : (
              <AccessDeniedView corporateMode={shellInfo.corporateMode} />
            ),
        })}
      </CreateJobDialog>
    )
  },
)

interface CreateJobDialogHeaderContainerProps {
  vm: CreateJobDialogViewModel
}

const CreateJobDialogHeaderContainer = observer(
  function CreateJobDialogHeaderContainer({
    vm,
  }: CreateJobDialogHeaderContainerProps) {
    return (
      <CreateJobDialogHeader
        pager={vm.modalHeader.pager}
        title={vm.modalHeader.title}
        description={vm.modalHeader.description}
        draftTitle={vm.draftTitle}
      />
    )
  },
)

interface BillingStepButtonGroupContainerProps {
  vm: CreateJobDialogViewModel
}

const BillingStepButtonGroupContainer = observer(
  function BillingStepButtonGroupContainer({
    vm,
  }: BillingStepButtonGroupContainerProps) {
    return (
      <CreateJobDialogBillingStepButtons
        onBack={vm.showQuestionsStep}
        canViewDetail={vm.canViewDetail}
        onViewDetail={vm.goDetail}
        onDone={vm.modal.close}
      />
    )
  },
)

interface QuestionsStepButtonGroupContainerProps {
  vm: CreateJobDialogViewModel
}

const QuestionsStepButtonGroupContainer = observer(
  function QuestionsStepButtonGroupContainer({
    vm,
  }: QuestionsStepButtonGroupContainerProps) {
    if (vm.isBulkEdit) {
      return (
        <CreateJobDialogBulkQuestionsStepButtons
          isBackShown={vm.isBackShown}
          onBack={vm.previousJob}
          onNext={vm.nextJob}
          nextLabel={vm.bulkEditActionButtonText}
        />
      )
    }

    return (
      <CreateJobDialogQuestionsStepButtons
        onClose={vm.modal.close}
        isBillingRelevant={vm.isBillingRelevant}
        isBillingStepReady={vm.isBillingStepReady}
        onNext={vm.showBillingStep}
        canViewDetail={vm.canViewDetail}
        onViewDetail={vm.goDetail}
      />
    )
  },
)

interface QuestionsUnansweredFeedbackContainerProps {
  vm: CreateJobDialogViewModel
}

const QuestionsUnansweredFeedbackContainer = observer(
  function QuestionsUnansweredFeedbackContainer({
    vm,
  }: QuestionsUnansweredFeedbackContainerProps) {
    return (
      <CreateJobDialogUnansweredFeedback
        isShown={vm.isUnansweredFeedbackShown}
        count={vm.incompleteQuestionsCount}
      />
    )
  },
)
